import { googleTagJsURL, triplabotVersion } from './Constants'
import triplaLocalStorage from 'Utilities/triplaLocalStorage'
import { getTriplaUID } from 'Utilities/triplaUID'
import { navigatorLanguage } from 'Utilities'
import { useUserStore } from 'Store/user'
import { useSettingsStore } from 'Store/settings'
import { useCustomerSegmentStore } from 'Store/customer-segment'
import { useCookieConsentStore } from 'Store/cookie-consent'
import { encrypGA4Value } from 'javascript/helpers/ga4'

class Gtag {
  constructor(settings) {
    this.settings = settings
    this.userStore = useUserStore()
    this.settingsStore = useSettingsStore()
    this.cookieConsentStore = useCookieConsentStore()
    this.customerSegmentStore = useCustomerSegmentStore()
    this.load()

    // disable tracking until users accept performance cookies
    // https://umami-me.atlassian.net/wiki/spaces/TECH/pages/3301016028/Chatbot+cookie+consent
    this.disableTracking()
  }

  load() {
    this.loadJs()
    window.dataLayer = window.dataLayer || []
  }

  gtag() {
    if (!this.isTrackingEnabled) return

    window.dataLayer.push(arguments)
  }

  disableTracking() {
    window[`ga-disable-${this.trackingId}`] = true
    window[`ga-disable-${this.ga4MeasurementID}`] = true
  }

  enableTracking() {
    window[`ga-disable-${this.trackingId}`] = false
    window[`ga-disable-${this.ga4MeasurementID}`] = false
  }

  get isTrackingEnabled() {
    if (
      this.cookieConsentStore.isCookiePreferencesConfirmed &&
      !this.cookieConsentStore.isPerformanceCookieEnabled
    )
      return false

    /* old version server doesn't have the value */
    /* disabled for development environment */
    return !!this.trackingId
  }

  /* asynchronous load. don't need to wait loading */
  loadJs() {
    const script = document.createElement('script')
    script.src = googleTagJsURL + '?id=' + this.trackingId
    document.head.appendChild(script)
  }

  get trackingId() {
    return this.settings.google_analytics_tracking_id
  }

  get ga4MeasurementID() {
    return this.settings.ga4_measurement_id || 'G-LB7LK68JQH'
  }

  recordIconMessageHidden(message) {
    this.recordEventIconMessage('Hide', message.id)
    this.trackGA4Event('icon_message_hidden', {
      segment_id: message.segment_id,
      hotel_brand_id: this.settingsStore.getBrandId,
      auto_message_id: message.id
    })
  }

  recordIconMessageClicked(message, iconAction) {
    this.recordEventIconMessage('Click', message.id, iconAction)
    let ga4EventParams = {
      segment_id: message.segment_id,
      hotel_brand_id: this.settingsStore.getBrandId,
      auto_message_id: message.id
    }

    if (message.segment_ids) {
      ga4EventParams = {
        ...ga4EventParams,
        utm_source: 'tripla Connect',
        utm_medium: 'icon automessage',
        utm_campaign: message.title
      }
      this.customerSegmentStore.setActiveIconMessage({
        segmentIds: message.segment_ids,
        iconMessageID: message.id,
        title: message.title
      })
    }

    this.trackGA4Event('icon_message_click', ga4EventParams)

    if (message.trigger_flow === 'booking_widget' && message.segment_ids) {
      this.customerSegmentStore.setSearchParams(message.search_params)
    }
  }

  recordIconMessageDisplayed(message) {
    this.recordEventIconMessage('Display', message.id)
    this.trackGA4Event('icon_message_display', {
      segment_id: message.segment_id,
      hotel_brand_id: this.settingsStore.getBrandId,
      auto_message_id: message.id
    })
  }

  recordChatMenuActionRoomReservationTriggered(action) {
    this.trackEvent(
      'room_reservation',
      'Room reservation',
      'Action auto message',
      action.menu_item_id
    )
    this.trackGA4Event('room_reservation', {
      event_category: 'Room reservation',
      event_label: 'Action auto message',
      value: action.menu_item_id
    })
  }

  recordChatMenuRoomReservationTriggered() {
    this.trackEvent('room_reservation', 'Room reservation', 'Chat menu')
    this.trackGA4Event('room_reservation', {
      event_category: 'Room reservation',
      event_label: 'Chat menu'
    })
  }

  recordIconActionRoomReservationTriggered(message) {
    this.trackEvent('room_reservation', 'Room reservation', 'Icon message', message.id)
    this.trackGA4Event('room_reservation', {
      event_category: 'Room reservation',
      event_label: 'Icon message',
      value: message.id,
      segment_id: message.segment_id
    })
  }

  recordPageview() {
    const hotelId = this.settings.hotel_id
    const userId = triplaLocalStorage.getItem('triplabot_user_id')
    const cookieId = getTriplaUID()

    this.gtag('js', new Date())
    const gtagPayload = {
      custom_map: {
        dimension1: 'bot_version',
        dimension2: 'biz_type',
        dimension3: 'customer_name',
        dimension4: 'member',
        dimension5: 'chat_id', // inactive
        dimension6: 'user_id',
        dimension7: 'hotel_id',
        dimension8: 'url',
        dimension9: 'booking_method' // used in BW only
      },
      bot_version: triplabotVersion,
      biz_type: this.settings.business_type,
      customer_name: this.settings.hotel_name,
      hotel_id: hotelId,
      cookie_id: cookieId,
      user_id: userId, // https://developers.google.com/analytics/devguides/collection/ga4/user-id
      groups: 'tripla',
      tripla_product_name: window._TRIPLA_PRODUCT_NAME || 'Bot'
    }

    const { custom_map, ...ga4Payload } = gtagPayload

    this.gtag('config', this.trackingId, gtagPayload)
    this.gtag('config', this.ga4MeasurementID, ga4Payload)
    this.gtag('set', 'user_properties', {
      known_user_id: this.userStore.isKnownUser ? userId : null
    })

    this.sendTriplaEvent()

    window.addEventListener('popstate', () => {
      this.sendTriplaEvent()
    })
  }

  sendTriplaEvent() {
    const triplaEventPayload = {
      page_category: window.location.href,
      user_id:
        triplaLocalStorage.getItem('triplabot_known_user_id') ||
        triplaLocalStorage.getItem('triplabot_user_id'),
      language: navigatorLanguage(),
      hotel_id: this.settings.hotel_id,
      hotel_name: this.settings.hotel_name
    }

    this.trackGA4Event('triplaChatbotEvent', triplaEventPayload)
    this.sendToHotelDataLayer(triplaEventPayload)
  }

  sendToHotelDataLayer(payload) {
    if (!window.dataLayer) return
    window.dataLayer.push({
      event: 'triplaEvent',
      ...payload
    })
  }

  triggerLabel(element) {
    switch (element.tagName) {
      case 'A':
        return 'Link'
      case 'BUTTON':
        return 'Button'
      case 'IMG':
        return 'Image'
      default:
        return 'Other'
    }
  }

  recordEventChatOpenByDataTriplabotToggle(element) {
    this.recordEventChatOpen('Standard trigger', this.triggerLabel(element))
  }

  recordEventChatOpenByDataTriplabotOpenMessage(element) {
    this.recordEventChatOpen('Phone trigger', this.triggerLabel(element))
  }

  recordEventChatOpen(action, label, value) {
    if (this.userStore.id) {
      this.trackEvent(action, 'Chat open', label, value)
    } else {
      // this is not ideal but it's too hard to try to sync with anonymous sign in
      setTimeout(() => this.trackEvent(action, 'Chat open', label, value), 2000)
    }
  }

  recordEventUserPolicy(action) {
    this.trackEvent(action, 'User Policy')
  }

  recordEventIconMessage(action, label, value) {
    this.trackEvent(action, 'Icon message', label, value)
  }

  recordEventAutoMessage(chat) {
    if (chat.message_rich) {
      const info = chat.message_rich.auto_message_info
      if (info) {
        this.trackEvent(info.type, 'Auto message', info.trigger)
      }
    }
  }

  recordEventFaqAutoMessageAction(locale, action) {
    this.trackEvent('FAQ reaction', 'Auto message', action.code + '_' + locale)
  }

  recordEventPhoneAutoMessageAction(locale) {
    this.trackEvent('Phone reaction', 'Auto message', locale)
  }

  getCommonParams() {
    return {
      membership_id: this.userStore.user.membership_id
        ? encrypGA4Value(this.userStore.user.membership_id)
        : null,
      tripla_internal_id: this.userStore.user.membership_customer_id
        ? encrypGA4Value(this.userStore.user.membership_customer_id)
        : null
    }
  }

  trackEvent(action, category, label, value) {
    this.gtag('event', action, {
      event_category: category,
      event_label: label,
      value,
      ...this.getCommonParams(),
      cookie_id: getTriplaUID(),
      send_to: 'tripla' // this property makes this method send the event to GA instead of GA4
    })
  }

  // https://developers.google.com/analytics/devguides/collection/gtagjs/events
  trackGA4Event(action, payload) {
    this.gtag('event', action, {
      ...payload,
      ...this.getCommonParams(),
      cookie_id: getTriplaUID(),
      send_to: this.ga4MeasurementID
    })
  }
}

export default Gtag
